class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.deferEmptyCartMedia();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) {
        event.preventDefault();
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
    // this.toggleShippingSwitch();
  }

  connectedCallback() {
    // this.toggleShippingSwitch();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  toggleShippingSwitch() {
    const toggle = this.querySelector('.js-addon-toggle');

    if (!toggle) return;

    const key = toggle.dataset.storageKey || `nx_addon_${toggle.dataset.variantId}`;
    const stored = localStorage.getItem(key);

    if (stored === '1') toggle.checked = true;
    if (stored === '0') toggle.checked = false;

    // 2) Then sync with cart (source of truth)
    //    If cart already has item -> force checked + storage
    //    If cart doesn't have item -> force unchecked + storage
    this.syncToggleWithCart(toggle);


    toggle.addEventListener('change', async () => {
      const variantId = Number(toggle.dataset.variantId);
      if (!variantId) return;

      // Save state
      localStorage.setItem(key, toggle.checked ? '1' : '0');

      // Prevent double taps while request runs
      toggle.disabled = true;

      try {
        if (toggle.checked) {
          // Temporary disable selling_plan inputs to prevent interceptors from grabbing them
          const spInputs = document.querySelectorAll('[name="selling_plan"]');
          const backupNames = [];
          spInputs.forEach(el => {
            backupNames.push(el.name);
            el.name = ""; 
          });

          await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
             method: 'POST',
             headers: {
               'Content-Type': 'application/json',
               'Accept': 'application/json'
             },
             body: JSON.stringify({
               id: Number(variantId),
               quantity: 1,
               selling_plan: null
             })
          })
          .then(res => res.json())
          .then(() => {})
          .finally(() => {
             // Restore inputs
             spInputs.forEach((el, i) => {
                el.name = backupNames[i];
             });
          });
        } else {
          await this.removeFromCart(variantId);
        }

        // After update, re-sync to confirm final state
        await this.syncToggleWithCart(toggle);
        // OPTIONAL: refresh cart drawer here
        // refreshCartDrawer();
      } catch (e) {
        console.error(e);

        // If request failed, revert UI to cart truth
        await this.syncToggleWithCart(toggle);
        localStorage.setItem(key, toggle.checked ? '1' : '0');
      } finally {
        toggle.disabled = false;
      }
    });


  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.showEmptyCartMedia();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
    // No class on <body>: base.css locks the page with body:has(cart-drawer.active).
    // A class change on <body> restyled every element of the page (TASKS #172).
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
  }

  // The empty-cart images stay display: none until showEmptyCartMedia() runs
  // (snippets/cart-drawer.liquid explains why; TASKS #143). Reveal them once
  // the page has loaded, so the first open still finds them cached, or sooner
  // when the customer points at or focuses the cart icon (pointerenter also
  // fires on touch, ahead of the tap). open() and clearLoading() reveal too.
  deferEmptyCartMedia() {
    const show = () => this.showEmptyCartMedia();
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.addEventListener('pointerenter', show, { once: true });
      cartLink.addEventListener('focusin', show, { once: true });
    }
    const whenIdle = () => {
      if ('requestIdleCallback' in window) requestIdleCallback(show, { timeout: 2000 });
      else setTimeout(show, 0);
    };
    if (document.readyState === 'complete') whenIdle();
    else window.addEventListener('load', whenIdle, { once: true });
  }

  showEmptyCartMedia() {
    // Adding a class that is already there still queues a mutation record, and
    // the snippet's class observer runs on each one.
    if (!this.classList.contains('cart-drawer--media-ready')) this.classList.add('cart-drawer--media-ready');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // Optimistic UI: open the drawer immediately in a loading state, BEFORE the
  // network add completes. The add still runs in the background; renderContents
  // reconciles the real cart HTML in when it lands. Focus is trapped after the
  // reconcile (not here) so we never trap focus on transient loading content.
  openLoading(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const inner = this.querySelector('.drawer__inner');
    // The skeleton lives in an inert <template> (#CartDrawerSkeletonTemplate) so
    // it's always in the DOM regardless of cart state — see snippets/cart-drawer.
    // Clone it INTO .drawer__inner (the position:relative panel) so the absolute
    // overlay is pinned to panel width, never escaping to the full-viewport
    // <cart-drawer>. Clear any stale clone from a previous open first.
    const tpl = this.querySelector('#CartDrawerSkeletonTemplate');
    const tplRoot = tpl && tpl.content && tpl.content.firstElementChild;
    this.querySelectorAll('.cart-drawer__skeleton, .cart-drawer__skel-row').forEach((el) => el.remove());

    // Cart state decides the loading UI:
    //   empty cart -> full skeleton overlay (nothing to show yet)
    //   populated  -> keep the real cart visible; append ONE skeleton item row
    //                 below the existing items (where the incoming item lands)
    const wasEmpty = this.classList.contains('is-empty');
    if (inner && tplRoot) {
      if (wasEmpty) {
        inner.appendChild(tplRoot.cloneNode(true));
        this.classList.add('cart-drawer--loading');
      } else {
        const row = tplRoot.querySelector('.cd-skel__item');
        if (row) {
          const wrap = document.createElement('div');
          wrap.className = 'cart-drawer__skel-row';
          wrap.setAttribute('aria-hidden', 'true');
          wrap.appendChild(row.cloneNode(true));
          // The new item is added to the TOP of the items list, so show the
          // skeleton row ABOVE the existing items (before #CartDrawer-Form, which
          // holds the real line-items table) so it pushes the current items down
          // to match where the incoming item lands. Fall back to prepending into
          // cart-drawer-items.
          const form = this.querySelector('#CartDrawer-Form');
          const itemsHost = this.querySelector('cart-drawer-items') || inner;
          if (form && form.parentElement) {
            form.insertAdjacentElement('beforebegin', wrap);
          } else if (itemsHost) {
            itemsHost.insertBefore(wrap, itemsHost.firstChild);
          }
        }
        this.classList.add('cart-drawer--adding');
      }
    } else {
      // Template missing (shouldn't happen) — still open with a loading flag.
      this.classList.add('cart-drawer--loading');
    }

    this.setAttribute('aria-busy', 'true');
    // Adding an item, so the cart is about to be non-empty: drop is-empty so the
    // panel uses the filled-cart layout/width during loading. renderContents sets
    // the correct is-empty from the server response; product-form restores it on
    // a failed add.
    this.classList.remove('is-empty');
    // Mirror open()'s setTimeout (not requestAnimationFrame): a macrotask
    // reliably triggers the slide-in transition AND still fires when the tab is
    // backgrounded — rAF is paused on hidden tabs, which would leave the drawer
    // stuck closed if the customer clicked add then tabbed away mid-request.
    setTimeout(() => this.classList.add('animate', 'active'));
  }

  clearLoading() {
    this.classList.remove('cart-drawer--loading', 'cart-drawer--adding');
    this.removeAttribute('aria-busy');
    this.querySelectorAll('.cart-drawer__skeleton, .cart-drawer__skel-row').forEach((el) => el.remove());
    // openLoading() leaves the empty-cart images hidden so they can't compete
    // with the add request. Once it settles, a cart emptied later in this
    // visit (or a failed add that restores the empty state) must show them.
    this.showEmptyCartMedia();
  }

  trapDrawerFocus() {
    const container = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    if (container && typeof trapFocus === 'function') trapFocus(container, focusElement);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    const sections = parsedState.sections || {};
    const wasOptimistic = this.classList.contains('cart-drawer--loading') || this.classList.contains('cart-drawer--adding');
    const isActive = this.classList.contains('active');

    // Swap ONLY .drawer__inner (the dynamic part). #CartDrawer-Overlay is a
    // sibling, so its constructor click-listener survives the swap — no
    // re-attach, no re-parse of the overlay. Parse the section HTML once.
    if (sections['cart-drawer']) {
      const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const newInner = parsed.querySelector('.drawer__inner');
      const liveInner = this.querySelector('.drawer__inner');
      if (newInner && liveInner) liveInner.innerHTML = newInner.innerHTML;
      const newRoot = parsed.querySelector('cart-drawer');
      if (newRoot) this.classList.toggle('is-empty', newRoot.classList.contains('is-empty'));
    }

    if (sections['cart-icon-bubble']) {
      const bubbleEl = document.getElementById('cart-icon-bubble');
      if (bubbleEl) {
        const parsedBubble = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newBubble = parsedBubble.querySelector('.shopify-section');
        if (newBubble) bubbleEl.innerHTML = newBubble.innerHTML;
      }
    }

    this.clearLoading();

    if (isActive) {
      // Optimistic path: drawer is already open and the content just settled.
      // The node that had focus may have been swapped out — re-trap focus.
      this.trapDrawerFocus();
    } else if (!wasOptimistic) {
      // Non-optimistic callers (bundle CTA, quick-add modal, skin-concern card):
      // open the drawer now that the content is in place.
      this.open();
    }
    // else: an optimistic open the customer closed mid-load — leave it closed.
  }

  async getCart() {
    const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const res = await fetch(rootUrl + 'cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  }

  async addToCarts(variantId, quantity, selling_plan) {
    const body = {
      id: Number(variantId),
      quantity: quantity
    };
    if (selling_plan !== undefined) {
      body.selling_plan = selling_plan;
    }
    await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        // TODO: refresh cart drawer / mini-cart here if needed
        // Example with some themes:
        // document.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch(function (err) {
        console.error('Error adding addon', err);
      });
  }

  async removeFromCart(variantId) {
    // update.js expects variantId keys as strings
    const updates = {};
    updates[variantId] = 0;
  
    await fetch((window.routes && window.routes.cart_update_url) || '/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        // TODO: refresh cart drawer / mini-cart
      })
      .catch(function (err) {
        console.error('Error removing addon', err);
      });
  
  }

  async syncToggleWithCart(toggle) {
    const variantId = Number(toggle.dataset.variantId);
    const key = toggle.dataset.storageKey || `nx_addon_${variantId}`;
  
    const cart = await this.getCart();
    const inCart = cart.items.some((item) => item.variant_id === variantId);
  
    toggle.checked = inCart;
    localStorage.setItem(key, inCart ? '1' : '0');
  }
  
  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* =========================================================
   SUBSCRIPTION UPGRADE — Cart Drawer
   Handles checkbox toggle + frequency dropdown for upgrading
   OTP items to subscriptions and vice versa.
========================================================= */
(function () {
  const CART_ADD_URL = window.routes?.cart_add_url || '/cart/add.js';
  const CART_CHANGE_URL = window.routes?.cart_change_url || '/cart/change.js';


  /**
   * Use XMLHttpRequest instead of fetch for cart API calls.
   * Appstle monkey-patches window.fetch and strips the selling_plan
   * parameter from request bodies. XHR is not intercepted.
   */
  function xhrPost(url, body) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.onload = function () {
        try {
          const data = JSON.parse(xhr.responseText);
          if (data.status && data.status >= 400) {
            reject(new Error(data.description || 'Cart API error'));
          } else {
            resolve(data);
          }
        } catch (e) {
          reject(e);
        }
      };
      xhr.onerror = function () { reject(new Error('Network error')); };
      xhr.send(JSON.stringify(body));
    });
  }

  async function removeCartLine(line) {
    return xhrPost(CART_CHANGE_URL, { line: line, quantity: 0 });
  }

  async function addToCart(variantId, quantity, sellingPlan) {
    // Use items[] array format to bypass Appstle's fetch/XHR interceptor
    // which strips selling_plan from the flat {id, quantity, selling_plan} format
    const item = { id: Number(variantId), quantity: quantity };
    if (sellingPlan) item.selling_plan = Number(sellingPlan);
    return xhrPost(CART_ADD_URL, { items: [item] });
  }

  // Debounced so rapid +/- clicks or chained subscription swaps coalesce into
  // a single section fetch + DOMParser pass instead of one per click.
  const refreshCartDrawer = debounce(function () {
    var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(rootUrl + '?sections=cart-drawer,cart-icon-bubble')
      .then((r) => r.json())
      .then((sectionHTML) => {
        if (sectionHTML['cart-drawer']) {
          const parsed = new DOMParser().parseFromString(sectionHTML['cart-drawer'], 'text/html');
          const sourceInner = parsed.querySelector('cart-drawer .drawer__inner');
          const targetInner = document.querySelector('cart-drawer .drawer__inner');
          if (targetInner && sourceInner) {
            targetInner.innerHTML = sourceInner.innerHTML;
          }
          const cartDrawer = document.querySelector('cart-drawer');
          const sourceCartDrawer = parsed.querySelector('cart-drawer');
          if (cartDrawer && sourceCartDrawer) {
            cartDrawer.classList.toggle('is-empty', sourceCartDrawer.classList.contains('is-empty'));
          }
        }
        if (sectionHTML['cart-icon-bubble']) {
          const bubbleEl = document.getElementById('cart-icon-bubble');
          if (bubbleEl) {
            const parsed = new DOMParser().parseFromString(sectionHTML['cart-icon-bubble'], 'text/html');
            const newBubble = parsed.querySelector('.shopify-section');
            if (newBubble) bubbleEl.innerHTML = newBubble.innerHTML;
          }
        }
      });
  }, 150);

  async function swapCartItem(variantId, quantity, line, sellingPlan) {
    try {
      await removeCartLine(line);
      try {
        await addToCart(variantId, quantity, sellingPlan);
      } catch (addErr) {
        // Add failed after remove — restore original item to prevent cart loss
        console.error('Subscription swap failed, restoring original item:', addErr);
        await addToCart(variantId, quantity, sellingPlan ? null : undefined);
      }
    } catch (err) {
      console.error('Cart subscription swap error:', err);
    }
    refreshCartDrawer();
  }

  /**
   * Ensure quantity respects the variant's quantity rules.
   * Snaps to the nearest valid value (>= min, multiple of increment).
   */
  function validQuantity(quantity, min, increment) {
    min = min || 1;
    increment = increment || 1;
    quantity = Math.max(quantity, min);
    if (increment > 1) {
      const remainder = (quantity - min) % increment;
      if (remainder !== 0) {
        quantity = quantity - remainder + increment;
      }
    }
    return quantity;
  }

  // Use capture phase to intercept the change event BEFORE it reaches
  // CartItems.onChange -> validateQuantity, which would treat the checkbox
  // as a quantity input and show "increments of" validation tooltip.
  document.addEventListener('change', function (e) {
    if (!e.target.classList.contains('cart-sub-upgrade__checkbox')) return;
    e.stopImmediatePropagation();
    e.stopPropagation();

    const wrapper = e.target.closest('.cart-sub-upgrade');
    if (!wrapper) return;

    const variantId = wrapper.dataset.variantId;
    const line = parseInt(wrapper.dataset.lineIndex, 10);
    const min = parseInt(wrapper.dataset.quantityMin, 10) || 1;
    const increment = parseInt(wrapper.dataset.quantityIncrement, 10) || 1;
    const quantity = validQuantity(parseInt(wrapper.dataset.quantity, 10), min, increment);
    const sellingPlan = wrapper.dataset.sellingPlan;

    if (e.target.checked) {
      swapCartItem(variantId, quantity, line, sellingPlan);
    } else {
      swapCartItem(variantId, quantity, line, null);
    }
  }, true);
})();

/* =========================================================
   ADDON TOGGLE — capture-phase propagation stopper
   .js-addon-toggle (shipping-protection / addon checkbox) sits inside
   <cart-drawer-items>. Its change events would otherwise bubble to
   CartItems.onChange -> validateQuantity, which treats them as quantity
   inputs (no dataset.index, NaN parse) and produces noisy validation.
   Mirror the .cart-sub-upgrade__checkbox capture-phase guard above.
========================================================= */
document.addEventListener('change', function (e) {
  if (e.target.classList && e.target.classList.contains('js-addon-toggle')) {
    e.stopImmediatePropagation();
  }
}, true);

// Empty-cart testimonial carousel. It used to be an inline script in
// snippets/cart-drawer.liquid: printed into every page's HTML when the cart was
// empty, and missing altogether when the page loaded with items and the cart
// was emptied later (the drawer re-renders with innerHTML, and a script inserted
// that way never runs). cart.js calls window.initCartEmptyTestimonials after
// emptying the cart.
(function () {
  window.__cartEmptySwiper = null;

  window.initCartEmptyTestimonials = function () {
    const el = document.querySelector('.cart-empty-testimonial-swiper');
    // Already built on this markup: nothing to do. cart.js calls this again
    // after emptying the cart, and the observer below on the drawer's open.
    if (window.__cartEmptySwiper && !window.__cartEmptySwiper.destroyed && window.__cartEmptySwiper.el === el) return;
    // Destroy the old instance if the markup was replaced.
    if (window.__cartEmptySwiper) {
      window.__cartEmptySwiper.destroy(true, true);
      window.__cartEmptySwiper = null;
    }
    if (!el || typeof Swiper === 'undefined') return;

    // loop: false. With only 3 slides Swiper silently disables loop mode (no
    // duplicate slides created), which made slideToLoop() a no-op and broke the
    // product-card hover sync. If more testimonials are ever added (5+ slides),
    // loop can be re-enabled and the hover handler switched back to slideToLoop.
    window.__cartEmptySwiper = new Swiper(el, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      autoHeight: true,
      pagination: {
        el: '.cart-empty-testimonial-pagination',
        clickable: true,
      },
    });

    // The arrows go to swiper-arrows.js, not Swiper's navigation, which disables
    // the end arrow and drops keyboard focus out of the open drawer (TASKS #161).
    if (window.JiYuSwiperArrows) {
      window.JiYuSwiperArrows.wire(window.__cartEmptySwiper, {
        prevEl: el.querySelector('.cart-empty-testimonial-prev'),
        nextEl: el.querySelector('.cart-empty-testimonial-next'),
      });
    }

    // Hover sync: product cards -> testimonial slides. slideTo, not slideToLoop,
    // because loop mode is off.
    const handleToIndex = {};
    el.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)').forEach((slide, idx) => {
      const h = slide.dataset.productHandle;
      if (h && !(h in handleToIndex)) handleToIndex[h] = idx;
    });

    document.querySelectorAll('.cart-empty__card[href]').forEach((card) => {
      const match = card.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (!match) return;
      const handle = match[1];
      if (handle in handleToIndex) {
        card.addEventListener('mouseenter', () => {
          window.__cartEmptySwiper.slideTo(handleToIndex[handle], 300);
        });
      }
    });
  };

  // Build when the drawer opens empty, not on page load: every new visitor's
  // cart starts empty, and the carousel sits below the drawer's first screen
  // (1,223 px down at 390 x 844). It used to be built on DOMContentLoaded and
  // then destroyed and rebuilt on every class change of the closed drawer,
  // ~13 ms each at 4x CPU. Building it just after the opening frame keeps that
  // out of the tap.
  // IMPORTANT: do NOT use subtree: true here. Swiper adds/removes classes on its
  // slides (.swiper-slide-active, -next, -prev) on every transition; a subtree
  // class observer would catch those and loop. Only the cart-drawer element's
  // own class matters.
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) return;
  new MutationObserver((mutations) => {
    const cartDrawerClassChanged = mutations.some(
      (m) => m.type === 'attributes' && m.attributeName === 'class' && m.target.tagName === 'CART-DRAWER'
    );
    if (!cartDrawerClassChanged) return;
    if (cartDrawer.matches('.is-empty.active') && document.querySelector('.cart-empty-testimonial-swiper')) {
      requestAnimationFrame(() => setTimeout(window.initCartEmptyTestimonials, 0));
    }
  }).observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
})();

// Smart upsell "Add" buttons in the cart drawer: one click delegate on the
// <cart-drawer> element, which outlives the drawer's re-renders. It used to be
// an inline script in snippets/cart-drawer.liquid, printed into every page's
// HTML and into every cart-drawer section render.
(function () {
  var drawer = document.querySelector('cart-drawer');
  if (!drawer || drawer.__smartUpsellBound) return;
  drawer.__smartUpsellBound = true;

  drawer.addEventListener('click', function (e) {
    var btn = e.target.closest('.smart-upsell-btn');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    // Locale-aware URLs, read at click time so subfolder markets like /en-ca
    // get their own routes.
    var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var cartUpdateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    var cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    if (btn.getAttribute('aria-disabled') === 'true') return;

    var variantId = parseInt(btn.dataset.variantId);
    var sellingPlan = btn.dataset.sellingPlan ? parseInt(btn.dataset.sellingPlan) : null;
    var removeKeys = btn.dataset.removeKeys.split(',').filter(function (k) { return k.length > 0; });
    var upsellSource = btn.dataset.upsellSource || 'cart_upsell';

    btn.classList.add('loading');
    btn.setAttribute('aria-disabled', 'true');

    var updates = {};
    removeKeys.forEach(function (key) { updates[key] = 0; });

    // Accept: application/json is REQUIRED. window.routes.cart_update_url
    // resolves to /cart/update (no .js suffix), and without an Accept header
    // Shopify returns the full HTML cart page instead of JSON. r.json() then
    // throws, the chain rejects, the spinner clears, and the add step below
    // never runs (the upsell silently fails to add).
    fetch(cartUpdateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        var addItem = { id: variantId, quantity: 1, properties: { '_source': upsellSource } };
        if (sellingPlan) addItem.selling_plan = sellingPlan;
        // Intelligems price test (TASKS #133): a shopper with a copy of this
        // product needs the copy's variant and plan, or Shopify rejects a
        // subscription add. See subscriptionItem() in assets/intelligems-bridge.js.
        var ig = window.JiyuIntelligems;
        return ig && typeof ig.subscriptionItem === 'function'
          ? ig.subscriptionItem(addItem, btn.dataset.productHandle)
          : addItem;
      })
      .then(function (addItem) {
        // XHR, not fetch: Appstle monkey-patched window.fetch and stripped
        // selling_plan from /cart/add.js bodies. See xhrPost above.
        return new Promise(function (resolve, reject) {
          var xhr = new XMLHttpRequest();
          xhr.open('POST', cartAddUrl);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('Accept', 'application/json');
          xhr.onload = function () {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch (err) { reject(err); }
          };
          xhr.onerror = function () { reject(new Error('Network error')); };
          xhr.send(JSON.stringify({ items: [addItem] }));
        });
      })
      .then(function (response) {
        if (response.status) {
          console.error('Upsell add error:', response.description);
          btn.classList.remove('loading');
          btn.removeAttribute('aria-disabled');
          return;
        }
        // Return this chain so a failed drawer refresh (network error, non-JSON
        // response, or a missing section payload) reaches the outer .catch,
        // which clears the spinner.
        return fetch(rootUrl + '?sections=cart-drawer')
          .then(function (r) { return r.json(); })
          .then(function (sections) {
            var html = sections['cart-drawer'];
            if (!html) throw new Error('cart-drawer section missing from refresh response');
            var tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;

            // Replace the entire .drawer__inner content for a full refresh.
            var sourceInner = tempDiv.querySelector('.drawer__inner');
            var targetInner = document.querySelector('.drawer__inner');
            if (targetInner && sourceInner) {
              targetInner.innerHTML = sourceInner.innerHTML;
            }

            // Update the cart-drawer is-empty state.
            var cartDrawer = document.querySelector('cart-drawer');
            var sourceCartDrawer = tempDiv.querySelector('cart-drawer');
            if (cartDrawer && sourceCartDrawer) {
              cartDrawer.classList.toggle('is-empty', sourceCartDrawer.classList.contains('is-empty'));
            }

            // Update the cart count bubble.
            var bubbles = document.querySelectorAll('.cart-count-bubble span[aria-hidden]');
            fetch(rootUrl + 'cart.js').then(function (r) { return r.json(); }).then(function (cart) {
              bubbles.forEach(function (b) { b.textContent = cart.item_count; });
            });

            // Re-bind overlay close.
            var overlay = document.getElementById('CartDrawer-Overlay');
            if (overlay && cartDrawer) {
              overlay.addEventListener('click', function () { cartDrawer.close(); });
            }

            btn.classList.remove('loading');
            btn.removeAttribute('aria-disabled');
          });
      })
      .catch(function (err) {
        console.error('Upsell swap error:', err);
        btn.classList.remove('loading');
        btn.removeAttribute('aria-disabled');
      });
  });
})();
